import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Star, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

interface FeaturedReview {
  id: string;
  rating: number;
  title: string;
  body: string;
  is_verified_purchase: boolean;
  product_name: string;
  product_slug: string;
  product_image?: string;
  first_name?: string;
  last_initial?: string;
  created_at: string;
}

function useFeaturedReviews() {
  return useQuery({
    queryKey: ['featured-reviews'],
    queryFn: () =>
      api.get<{ reviews: FeaturedReview[] }>('/products/featured-reviews'),
    staleTime: 1000 * 60 * 30,
  });
}

export default function ReviewCarousel() {
  const { t } = useTranslation('product');
  const { data, isLoading } = useFeaturedReviews();
  const scrollRef = useRef<HTMLDivElement>(null);

  const reviews = data?.reviews ?? [];

  if (isLoading || reviews.length === 0) return null;

  function scroll(direction: 'left' | 'right') {
    if (!scrollRef.current) return;
    const distance = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  }

  return (
    <section className="py-12" aria-label={t('reviewCarousel.sectionAriaLabel')}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-display font-bold text-ink">
            {t('reviewCarousel.heading')}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="rounded-full p-2 border border-line hover:bg-surface-soft transition-colors"
              aria-label={t('reviewCarousel.scrollLeft')}
            >
              <ChevronLeft className="h-4 w-4 text-ink-soft" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="rounded-full p-2 border border-line hover:bg-surface-soft transition-colors"
              aria-label={t('reviewCarousel.scrollRight')}
            >
              <ChevronRight className="h-4 w-4 text-ink-soft" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 -mx-4 px-4"
          role="list"
        >
          {reviews.map((review) => (
            <article
              key={review.id}
              className="snap-start shrink-0 w-[300px] rounded-xl border border-line bg-surface p-5 shadow-sm"
              role="listitem"
            >
              {/* Stars */}
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    className={cn(
                      'h-4 w-4',
                      i < review.rating
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-ink-muted'
                    )}
                    aria-hidden="true"
                  />
                ))}
              </div>

              {/* Title */}
              {review.title && (
                <h3 className="mt-2 font-semibold text-ink text-sm line-clamp-1">
                  {review.title}
                </h3>
              )}

              {/* Body */}
              <p className="mt-1.5 text-sm text-ink-soft line-clamp-3">
                {review.body}
              </p>

              {/* Reviewer */}
              <div className="mt-3 flex items-center gap-2">
                <p className="text-xs text-ink-muted">
                  {review.first_name
                    ? `${review.first_name} ${review.last_initial || ''}`
                    : t('reviewCarousel.anonymous')}
                </p>
                {review.is_verified_purchase && (
                  <span className="inline-flex items-center gap-0.5 text-xs text-success-700">
                    <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                    {t('reviewCarousel.verified')}
                  </span>
                )}
              </div>

              {/* Product link */}
              <Link
                to={`/product/${review.product_slug}`}
                className="mt-3 flex items-center gap-2 text-xs text-ink-muted hover:text-sage-700 transition-colors"
              >
                {review.product_image && (
                  <img
                    src={review.product_image}
                    alt=""
                    className="w-8 h-8 rounded object-cover"
                    loading="lazy"
                  />
                )}
                <span className="line-clamp-1">{review.product_name}</span>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
